import React, { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { cvBreakpoint768 } from '@carvana/theme';
import { ThemeProvider } from '@carvana/theme';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  DashboardModalActions,
  useIsInBucket
} from '@carvana/purchase-helpers';
import { CarvanaTheme, getMediaQuery } from '@carvana/showroom-theme';
import { ModalContent } from '@carvana/modal';
import {
  CancelOrderModal,
  EditOrderModal
} from '@carvana/purchase-order-placed';
import VehicleSnapshot from './components/vehicle-snapshot';
import NavTab from './components/navigation-tab';
import { CloseIcon16px } from '~/library/icons/src';
import { SupportContactButton } from '../supportcontact';

type NavItem = {
  id: string;
  route: string;
  navigation?: boolean;
};

type Section = {
  id: string;
  header: string;
  status: string;
  containsNavItems?: boolean;
};

const navSectionStyles = css`
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.carvana.gray.light};
  display: flex;
  padding: 0 30px;
`;

const Wrapper = styled.div<{ isOpen: boolean }>`
  background-color: white;
  width: 80%;
  z-index: 9999;
  position: fixed;
  right: 0;
  overflow: auto;
  height: 100vh;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${getMediaQuery(CarvanaTheme.breakpoint.md)} {
    max-width: 320px;
    padding-bottom: 84px;
  }

  ${getMediaQuery(CarvanaTheme.breakpoint.xl)} {
    max-width: 336px;
  }

  ${getMediaQuery(CarvanaTheme.breakpoint.xxl)} {
    max-width: 384px;
  }

  @media (max-width: ${cvBreakpoint768}px) {
    padding-bottom: 60px;
  }

  transform: ${props => (props.isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease-out;
`;

const Section = styled.section``;

const SectionHeader = styled.div`
  ${navSectionStyles}
  font-size: 14px;
  height: 56px;

  > span:first-of-type {
    color: ${props => props.theme.carvana.blue.dark};
    font-weight: bold;
    text-transform: uppercase;
  }

  ${Section}.disabled & {
    color: ${props => props.theme.carvana.gray.medium};
  }
`;

const SectionStatusWrapper = styled.div`
  align-items: center;
  color: ${props => props.theme.carvana.blue.primary};
  display: flex;
  margin-left: auto;

  ${Section}.disabled & {
    color: ${props => props.theme.carvana.gray.medium};
  }
`;

const SectionStatusText = styled.p`
  color: ${props => props.theme.carvana.gray.dark};
`;

const NavTabsWrapper = styled.div``;

const CancelAction = styled.div`
  ${navSectionStyles}

  border-bottom: none;
  color: ${props => props.theme.carvana.gray.dark};
  font-size: 14px;
  cursor: pointer;
  height: 56px;
`;

const EditAction = styled.div`
  ${navSectionStyles}

  border-bottom: none;
  color: ${props => props.theme.carvana.gray.dark};
  font-size: 14px;
  cursor: pointer;
  height: 56px;
`;

const Content = styled(ModalContent)`
  z-index: 1301;

  &.loading {
    overflow-y: visible;
  }
`;

const CancelOrderText = styled.span`
  color: ${props => props.theme.carvana.blue.primary};
`;

const EditOrderText = styled.span`
  color: ${props => props.theme.carvana.blue.primary};
`;

const TopSection = styled.div`
  height: 48px;
  border-bottom: 1px solid ${props => props.theme.carvana.gray.light};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const CloseIcon = styled(CloseIcon16px)`
  margin-right: 16px;
`;

const SupportContactWrapper = styled.div`
  display: flex;
  position: absolute;
  left: 0;
  margin-left: 16px;
`;

const { OpenModal } = DashboardModalActions;

const defaultProps = {
  navItems: [] as NavItem[],
  navSections: [] as Section[],
  navRail: false,
  isOpen: false,
  setIsNavOpen: (_isOpen: boolean) => {},
  menuRef: React.createRef<HTMLDivElement>(),
  setIsSupportMfeOpen: (_isOpen: boolean) => {}
};

const isNativeWebview = /CarvanaNativeWebView/gi.test(navigator?.userAgent);

const PurchaseMenu = ({
  navItems,
  navSections,
  navRail,
  isOpen,
  setIsNavOpen,
  menuRef,
  setIsSupportMfeOpen,
} = defaultProps) => {
  const [, , route] = window.location.pathname.split('/');
  const navigationItems = navItems?.filter(x => x.navigation);
  const showNavRail = !!navigationItems && navRail;
  const { trackEvent } = useContext(AnalyticsContext);

  const isInFinalizePurchaseBucket = useIsInBucket(
    'pp-finalize-purchase-edit-button',
    'edit-order-doc-upload',
    ['control']
  );

  const isInPurchaseHeaderBucket = useIsInBucket(
    'pp-purchase-header-dec-2023',
    'purchase-header',
    ['control']
  );

  const openCancelOrderModal = () => {
    trackEvent('pp_clicked_cancel_purchase');
    setIsNavOpen(false);
    OpenModal({
      headerText: 'CANCEL ORDER',
      ModalChildren: CancelOrderModal,
      modalProps: Content
    });
  };

  const openEditOrderModal = () => {
    setIsNavOpen(false);
    OpenModal({
      headerText: 'EDIT ORDER',
      ModalChildren: EditOrderModal,
      modalProps: Content
    });
  };

  useEffect(() => {
    const closeMenuOnBack = () => {
      setIsNavOpen(false);
    };

    window.addEventListener('popstate', closeMenuOnBack);

    return () => {
      window.removeEventListener('popstate', closeMenuOnBack);
    };
  }, []);

  return (
    <ThemeProvider>
      {showNavRail && (
        <Wrapper isOpen={isOpen} ref={menuRef}>
          <TopSection>
            {isInPurchaseHeaderBucket && !isNativeWebview && (
              <SupportContactWrapper>
                <SupportContactButton setIsOpen={setIsSupportMfeOpen} />
              </SupportContactWrapper>
            )}
            <CloseIcon
              onClick={() => {
                setIsNavOpen(false);
                if (menuRef.current) {
                  menuRef.current.scrollTop = 0;
                }
              }}
            />
          </TopSection>
          <VehicleSnapshot />
          {navSections?.map(section => (
            <Section key={section.id}>
              <SectionHeader>
                <span onClick={e => e.stopPropagation()}>{section.header}</span>
                {section.header === 'Purchase Process' &&
                  section.status === 'Done' &&
                  isInFinalizePurchaseBucket && (
                    <EditAction onClick={openEditOrderModal}>
                      <EditOrderText>Edit</EditOrderText>
                    </EditAction>
                  )}
                <SectionStatusWrapper onClick={e => e.stopPropagation()}>
                  <SectionStatusText>{section.status}</SectionStatusText>
                </SectionStatusWrapper>
              </SectionHeader>
              {section.containsNavItems && (
                <NavTabsWrapper>
                  {navItems?.map(navItem => (
                    <NavTab
                      isActive={`/${route}` === navItem.route}
                      key={navItem.id}
                      navItem={navItem}
                      setIsNavOpen={setIsNavOpen}
                      menuRef={menuRef}
                    />
                  ))}
                </NavTabsWrapper>
              )}
            </Section>
          ))}
          <Section>
            <CancelAction onClick={openCancelOrderModal}>
              <CancelOrderText>Want to cancel your order?</CancelOrderText>
            </CancelAction>
          </Section>
        </Wrapper>
      )}
    </ThemeProvider>
  );
};

export default PurchaseMenu;
